// src/BostaPage.js
import React, { useState, useEffect } from 'react';
import { db, auth } from './firebase';
import { 
  collection, 
  query, 
  where, 
  getDocs,
  doc,
  updateDoc 
} from 'firebase/firestore';
import { useNavigate, useLocation } from 'react-router-dom';
import { useI18n } from './i18n';
import './BostaPage.css';

function BostaPage() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const uidFromUrl = searchParams.get('uid');
  const adminFromUrl = searchParams.get('admin');
  const isSuperAdminView = !!adminFromUrl;
  
  const [currentUser, setCurrentUser] = useState(null);
  const [clientInfo, setClientInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // بيانات الفورم
  const [bostaApiKey, setBostaApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  // التحقق من تسجيل الدخول وجلب بيانات العميل
  useEffect(() => {
    // 🔥 لو جاي من السوبر أدمن، استخدم uid من الـ URL
    if (uidFromUrl) {
      fetchClientData(uidFromUrl);
      return;
    }
    
    // لو عميل عادي، استخدم auth
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setCurrentUser(user);
        await fetchClientData(user.uid);
      } else {
        navigate('/');
      }
    });
    return () => unsubscribe();
  }, [navigate, uidFromUrl]);

  const fetchClientData = async (uid) => {
    try {
      const clientQuery = query(
        collection(db, 'clients_api'), 
        where('auth_uid', '==', uid)
      );
      const snapshot = await getDocs(clientQuery);
      
      if (!snapshot.empty) {
        const data = snapshot.docs[0].data();
        setClientInfo({ ...data, docId: snapshot.docs[0].id });
        setBostaApiKey(data.bosta_api || '');
      } else {
        setMessage(t('bosta.page.clientNotFound'));
      }
    } catch (error) {
      console.error('Error fetching client data:', error);
      setMessage(t('bosta.page.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!(bostaApiKey || '').trim()) {
      setMessage(t('bosta.page.apiKeyRequired'));
      return;
    }

    setSaving(true);
    setMessage('');

    try {
      const clientDocRef = doc(db, 'clients_api', clientInfo.brand_name);
      await updateDoc(clientDocRef, {
        bosta_api: (bostaApiKey || '').trim()
      });

      setMessage(t('bosta.page.saveSuccess'));
      setTimeout(() => setMessage(''), 3000);
    } catch (error) {
      console.error('Error saving API key:', error);
      setMessage(t('bosta.page.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleCopyWebhook = () => {
    const webhookUrl = `https://n8n2.dafaiagency.shop/webhook/65b873aa-5c25-4a75-a3f7-10b07835a9c9/bosta-hook-alsayes3/${clientInfo.webhook_path_id}`;
    navigator.clipboard.writeText(webhookUrl);
    setMessage(t('bosta.page.webhookCopied'));
    setTimeout(() => setMessage(''), 2000);
  };

  if (loading) {
    return (
      <div className="bosta-container">
        <div className="loading">{t('loading.app')}</div>
      </div>
    );
  }

  if (!clientInfo) {
    return (
      <div className="bosta-container">
        <div className="error-message">{t('bosta.page.clientNotFound')}</div>
      </div>
    );
  }

  const webhookUrl = `https://n8n2.dafaiagency.shop/webhook/65b873aa-5c25-4a75-a3f7-10b07835a9c9/bosta-hook-alsayes3/${clientInfo.webhook_path_id}`;

  return (
    <div className="bosta-container" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      <div className="bosta-card">
        {/* Header */}
        <button 
          onClick={() => navigate('/dashboard' + location.search)} 
          className="back-button"
        >
          {lang === 'ar' ? '→' : '←'} {t('common.back') || (lang === 'ar' ? 'رجوع' : 'Back')}
        </button>
        <div className="bosta-header">
          <h1 className="bosta-title">{t('bosta.page.title')}</h1>
        </div>

        {/* Webhook Section */}
        <div className="section">
          <h2 className="section-title">{t('bosta.page.webhookTitle')}</h2>
          <p className="section-description">
            {t('bosta.page.webhookDescription')}
          </p>
          
          <div className="webhook-box">
            <code className="webhook-url">{webhookUrl}</code>
          </div>

          <button 
            onClick={handleCopyWebhook}
            className="copy-button"
          >
            {t('bosta.page.copyLink')}
          </button>
        </div>

        {/* API Key Section */}
        <div className="section">
          <h2 className="section-title">{t('bosta.page.apiKeyTitle')}</h2>
          <p className="section-description">
            {t('bosta.page.apiKeyDescription')}
          </p>

          <input
            type="text"
            value={bostaApiKey}
            onChange={(e) => setBostaApiKey(e.target.value)}
            placeholder={t('bosta.page.apiKeyPlaceholder')}
            className="api-input"
            disabled={saving}
          />

          <button 
            onClick={handleSave}
            disabled={saving || !(bostaApiKey || '').trim()}
            className="save-button"
          >
            {saving ? t('bosta.page.saving') : t('bosta.page.save')}
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`message ${message.includes('✅') ? 'success' : message.includes('❌') ? 'error' : 'warning'}`}>
            {message}
          </div>
        )}

        {/* Info */}
        <div className="info-box">
          <p><strong>{t('bosta.page.noteTitle')}</strong></p>
          <p>• {t('bosta.page.note1')}</p>
          <p>• {t('bosta.page.note2')}</p>
          <p>• {t('bosta.page.note3')}</p>
        </div>
      </div>
    </div>
  );
}

export default BostaPage;
